import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ErrorAlert, Header, LinkBtn } from "@";
import { useWatch } from "react-hook-form";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  CodeBracketIcon,
  EyeIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline";
import { useThemeForm } from "../../hooks/index";
import {
  ThemeInfoSection,
  ThemeTemplateSection,
} from "../../components/form-sections";
import { ThemeVisualPreview } from "../../components/ThemeVisualPreview";

type ViewMode = "code" | "split" | "preview";

const VIEW_MODES: {
  id: ViewMode;
  label: string;
  hint: string;
  Icon: typeof CodeBracketIcon;
}[] = [
  {
    id: "code",
    label: "الكود",
    hint: "تركيز على المحرر",
    Icon: CodeBracketIcon,
  },
  {
    id: "split",
    label: "مقسّم",
    hint: "كود ومعاينة جنبًا إلى جنب",
    Icon: ViewColumnsIcon,
  },
  {
    id: "preview",
    label: "المعاينة",
    hint: "ملء الشاشة للنتيجة",
    Icon: EyeIcon,
  },
];

const Page = () => {
  const {
    register,
    setValue,
    getValues,
    control,
    errors,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
    edit,
    show,
    id,
    error,
    links,
    submit,
    handleUpload,
  } = useThemeForm();

  const themeContent =
    (useWatch({ control, name: "themeContent" }) as string) ?? "";
  const isPublished = useWatch({ control, name: "isPublished" }) as
    | boolean
    | undefined;

  const [viewMode, setViewMode] = useState<ViewMode>(id ? "preview" : "split");
  const showEditor = viewMode !== "preview";
  const showPreview = viewMode !== "code";

  // Fullscreen wraps the entire studio (toolbar + editor + preview), so the
  // user gets the full viewport for both panels at once.
  const studioRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === studioRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const node = studioRef.current;
    if (!node) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await node.requestFullscreen();
      }
    } catch {
      // Permission denials / unsupported contexts — fail silently.
    }
  }, []);

  return (
    <>
      <ErrorAlert show={error} />

      <Header
        flex
        title={`${edit ? "تعديل الثيم" : show ? "عرض الثيم" : "اضافة ثيم"}`}
        links={links}
      >
        {show && <LinkBtn to={`/website/themes/${show}/edit`} edit />}
      </Header>

      <form
        onSubmit={handleSubmit(submit)}
        className="relative flex flex-col gap-3"
      >
        <ThemeInfoSection
          register={register}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          handleUpload={handleUpload}
          isSubmitSuccessful={isSubmitSuccessful}
          show={show}
        />

        {/* Studio — toolbar + stage. Wrapped together so fullscreen covers
            both code editor and preview, not just one panel. */}
        <div
          ref={studioRef}
          className={`flex flex-col bg-white gap-3  p-4 dark:bg-gray-950 ${
            isFullscreen ? "h-screen overflow-auto " : "rounded-2xl border border-gray-200 dark:border-gray-700"
          }`}
        >
          {/* Studio toolbar — switch between Code / Split / Preview */}
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white/85 px-4 py-3 backdrop-blur-md dark:border-gray-700 dark:bg-gray-900/85 ${
              isFullscreen ? "sticky top-0 z-30" : "sticky top-0 z-30"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span className="flex size-8 items-center justify-center rounded-lg bg-purple-900 text-white dark:bg-purple-500">
                <CodeBracketIcon className="size-4" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  استوديو الثيم
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {VIEW_MODES.find((m) => m.id === viewMode)?.hint}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div
                role="tablist"
                aria-label="وضع العرض"
                className="inline-flex items-center gap-1 rounded-2xl bg-gray-100 p-1 dark:bg-gray-800"
              >
                {VIEW_MODES.map((m) => {
                  const Icon = m.Icon;
                  const isActive = m.id === viewMode;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setViewMode(m.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                        isActive
                          ? "bg-white text-purple-900 shadow-sm dark:bg-gray-700 dark:text-purple-300"
                          : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      }`}
                    >
                      <Icon className="size-4" />
                      <span className="hidden sm:inline">{m.label}</span>
                    </button>
                  );
                })}
              </div>

              <span
                aria-hidden
                className="hidden h-6 w-px bg-gray-200 dark:bg-gray-700 sm:inline-block"
              />

              <button
                type="button"
                onClick={toggleFullscreen}
                aria-pressed={isFullscreen}
                title={isFullscreen ? "إنهاء ملء الشاشة" : "ملء الشاشة"}
                className={`inline-flex size-9 items-center justify-center rounded-2xl border transition ${
                  isFullscreen
                    ? "border-purple-300 bg-purple-900 text-white hover:bg-purple-950 dark:border-purple-500 dark:bg-purple-500"
                    : "border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-purple-500 dark:hover:text-purple-300"
                }`}
              >
                {isFullscreen ? (
                  <ArrowsPointingInIcon className="size-4" />
                ) : (
                  <ArrowsPointingOutIcon className="size-4" />
                )}
              </button>
            </div>
          </div>

          {/* Studio stage */}
          <div
            className={`grid gap-3 transition-all duration-300 ${
              viewMode === "split" ? "xl:grid-cols-2" : "grid-cols-1"
            } ${isFullscreen ? "flex-1" : ""}`}
          >
            {showEditor && (
              <div
                className={`min-w-0 ${
                  viewMode === "split"
                    ? "order-2 xl:order-1"
                    : "animate-[fadeIn_.25s_ease-out]"
                }`}
              >
                <ThemeTemplateSection
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  show={show}
                />
              </div>
            )}

            {showPreview && (
              <div
                className={`min-w-0 ${
                  viewMode === "split"
                    ? "order-1 xl:sticky xl:top-[5.25rem] xl:order-2 xl:h-[calc(100vh-6rem)]"
                    : isFullscreen
                      ? "h-[calc(100vh-7rem)] animate-[fadeIn_.25s_ease-out]"
                      : "h-[calc(100vh-10rem)] animate-[fadeIn_.25s_ease-out]"
                }`}
              >
                <ThemeVisualPreview
                  rawCode={themeContent}
                  isPublished={isPublished}
                />
              </div>
            )}
          </div>
        </div>

        {!show && (
          <div className="floating-btn">
            <Button
              className="w-full"
              isLoading={isSubmitting}
              type="submit"
              title="حفظ البيانات الثيم"
            />
          </div>
        )}
      </form>
    </>
  );
};

export default Page;
